import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField, { OptionProps } from '../../molecules/SelectTextField';
import Loading from '../../ions/Loading';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import { COLLATERAL_BREADCRUMBS, formatCurrency } from './collateralUtils';
import GuaranteeStatusBadge from './GuaranteeStatus';
import ValidationStatusBadge from './ValidationStatus';

const toOptions = (items: string[]): OptionProps[] => items.map(i => ({ value: i, label: i }));

const CRITERIA_OPTIONS = toOptions(['Completa', 'Amostragem', 'N/A']);
const CONTRACT_REGISTER_OPTIONS = toOptions(['CF DE RECEBÍVEIS', 'CF DA CONTA ESCROW', 'ADITAMENTO']);

const SectionTitle = ({ children, color }: { children: string; color?: string }) => (
    <h3 className={`text-[10px] font-semibold uppercase mb-3 tracking-wide ${color || 'text-gray-500 dark:text-gray-400'}`}>{children}</h3>
);

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex justify-between items-center py-1">
        <span className="text-gray-500 dark:text-gray-400 text-xs">{label}</span>
        <span className="text-xs dark:text-white text-right max-w-[60%] truncate" title={String(value || '')}>{value || '—'}</span>
    </div>
);

const Checkbox = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
    <label className="flex items-center gap-2 cursor-pointer py-1">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
        <span className="text-xs dark:text-white">{label}</span>
    </label>
);

const GuaranteePageForm = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [form, setForm] = useState<any>({});
    const [analystSearch, setAnalystSearch] = useState('');
    const [analystResults, setAnalystResults] = useState<any[]>([]);

    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        collateralApi.getGuaranteeView(id)
            .then(setForm)
            .catch((e: any) => {
                if (e instanceof ApiError && e.status === 404) return navigate('/not-found');
                navigate('/error');
            })
            .finally(() => setIsLoading(false));
    }, [id]);

    const set = (field: string, value: any) => setForm((prev: any) => ({ ...prev, [field]: value }));
    const ev = (field: string) => (e: any) => set(field, e.target.value);

    const searchAnalyst = async (query: string) => {
        if (query.length < 3) { setAnalystResults([]); return; }
        try {
            const res = await collateralApi.searchPerson(query);
            setAnalystResults(res || []);
        } catch { setAnalystResults([]); }
    };

    const handleSave = async () => {
        try {
            await collateralApi.updateGuarantee(id!, form);
            toast.success('Garantia atualizada!');
            navigate(`/collateral/guarantees/${id}/view`);
        } catch {
            toast.error('Erro ao salvar');
        }
    };

    if (isLoading) return <Loading />;

    return (
        <PageForm
            pageTitle={`Editar — ${form.operationCode || ''}`}
            apiVersion={form.apiVersion ? `${form.apiName} - ${form.apiVersion} (${(form.environment || '').toUpperCase()})` : ''}
            isLoading={false}
            breadcrumb={[
                COLLATERAL_BREADCRUMBS.guarantees,
                { label: form.operationCode, url: `/collateral/guarantees/${id}/view` },
                { label: 'Editar', url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end space-x-2">
                    <button onClick={() => navigate(`/collateral/guarantees/${id}/view`)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600 text-sm">
                        Cancelar
                    </button>
                    <button onClick={handleSave}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm flex items-center space-x-2">
                        <FiSave size={14} /> <span>Salvar</span>
                    </button>
                </div>
            }
        >
            <div className="grid grid-cols-[2fr_1fr] gap-4 my-4">
                {/* Configuração */}
                <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-4 border border-green-200 dark:border-green-800">
                    <SectionTitle color="text-green-700 dark:text-green-400">Configuração</SectionTitle>
                    <div className="grid grid-cols-2 gap-x-6 divide-x divide-green-200 dark:divide-green-800">
                        <div className="space-y-3">
                            <Field label="Tipo - Obrigação" value={`${form.operationType} - ${({'WITH_RECOURSE':'Com regresso','WITHOUT_RECOURSE':'Sem regresso','NO_OBLIGATION':'Sem garantia'} as Record<string,string>)[form.obligation] || form.obligation || '—'}`} />
                            <Field label="Cedente" value={form.assignor} />
                            <Field label="CNPJ Cedente" value={form.assignorTaxId} />
                            <Field label="Intensidade" value={form.analysisIntensity || '—'} />
                            <SelectTextField label="Critério análise" value={form.analysisCriteria || ''} items={CRITERIA_OPTIONS} onChange={ev('analysisCriteria')} />
                            <TextEdit label="Títulos amostragem" value={String(form.sampledTitles || '')} onChange={ev('sampledTitles')} />
                            <Field label="Data emissão" value={form.issueDate?.split('T')[0] || '—'} />
                        </div>
                        <div className="space-y-3 pl-6">
                            <div className="relative">
                                <TextEdit label="Resp. análise (Enter para buscar)" value={analystSearch || form.analystName || ''}
                                    onChange={e => setAnalystSearch(e.target.value)}
                                    onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); searchAnalyst(analystSearch); } }} />
                                {analystResults.length > 0 && (
                                    <div className="absolute z-50 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded shadow-lg max-h-32 overflow-y-auto">
                                        {analystResults.map((p: any) => (
                                            <button key={p.id} onClick={() => { set('analystName', p.name); setAnalystSearch(''); setAnalystResults([]); }}
                                                className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 dark:text-white">
                                                {p.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Field label="Trader" value={form.trader} />
                            <TextEdit label="Pendência" value={form.pendency || ''} onChange={ev('pendency')} />
                            <TextEdit label="Observação" value={form.observation || ''} onChange={ev('observation')} />
                        </div>
                    </div>
                </div>

                {/* Right: Resumo + Status */}
                <div className="flex flex-col gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                        <SectionTitle color="text-blue-700 dark:text-blue-400">Resumo (somente leitura)</SectionTitle>
                        <div className="space-y-1 text-xs">
                            <div className="flex justify-between"><span className="text-gray-500">Nº títulos</span><span>{form.totalTitles}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Moeda</span><span>{form.currency || 'BRL'}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Total</span><span>{formatCurrency(form.totalAmount || 0)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">% validado</span><span>{form.validatedPercentage || 0}%</span></div>
                        </div>
                    </div>

                    <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                        <SectionTitle color="text-amber-700 dark:text-amber-400">Status (somente leitura)</SectionTitle>
                        <div className="space-y-1">
                            <div className="flex justify-between items-center py-0.5">
                                <span className="text-gray-500 dark:text-gray-400 text-xs">Status garantia</span>
                                <GuaranteeStatusBadge status={form.guaranteeStatus} />
                            </div>
                            <div className="flex justify-between items-center py-0.5">
                                <span className="text-gray-500 dark:text-gray-400 text-xs">Status validação</span>
                                <ValidationStatusBadge status={form.validationStatus} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controles booleanos */}
            <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
                    <Checkbox label="Lastro salvo em garantias" checked={!!form.collateralSaved} onChange={v => set('collateralSaved', v)} />
                </div>
                <div className={`p-3 border rounded space-y-2 ${form.debtorNotificationRequested ? 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800' : 'bg-gray-100 dark:bg-slate-900 border-gray-200 dark:border-slate-700 opacity-60'}`}>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Notificação de sacados solicitada: <strong>{form.debtorNotificationRequested ? 'Sim' : 'Não'}</strong></span>
                    {form.debtorNotificationRequested && (
                        <Checkbox label="Notificação realizada" checked={!!form.debtorNotificationDone} onChange={v => set('debtorNotificationDone', v)} />
                    )}
                </div>
                <div className="p-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded space-y-2">
                    <Checkbox label="Registro em cartório" checked={!!form.courtRegistration} onChange={v => set('courtRegistration', v)} />
                    {form.courtRegistration && (
                        <SelectTextField label="Qual contrato registrar?" value={form.contractToRegister} items={CONTRACT_REGISTER_OPTIONS} onChange={ev('contractToRegister')} />
                    )}
                </div>
            </div>
        </PageForm>
    );
};

export default GuaranteePageForm;
